<template>
    <div>
        <!-- Filter Section -->
        <div class="filters-section">
            <h3 class="filters-title">
                <span>
                    <q-icon name="tune" class="filter-icon" />
                    Filters
                </span>
            </h3>

            <q-expansion-item class="expansion-panel" label="Date Ranges" switch-toggle-side
                expand-icon="keyboard_arrow_down" expanded-icon="keyboard_arrow_up" header-class="expansion-header">
                <div class="expansion-content">
                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Form Completed Date From</label>
                            <q-input class="form-control-quasar" outlined label-color="black" v-model="startDate"
                                mask="####-##-##" :placeholder="dateFormat" clearable
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="startDate" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                            <q-tooltip class="text-body2">
                                Filter SROI data from this date onwards
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Form Completed Date To</label>
                            <q-input class="form-control-quasar" outlined label-color="black" v-model="endDate"
                                mask="####-##-##" :placeholder="dateFormat" clearable
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="endDate" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                            <q-tooltip class="text-body2">
                                Filter SROI data up to this date
                            </q-tooltip>
                        </div>
                    </div>
                </div>
            </q-expansion-item>
            
            <div class="filter-actions">
                <q-btn class="btn btn-secondary" @click="resetFilters" outline color="grey-7" icon="refresh"
                    label="Reset" no-caps />
                <q-btn class="btn btn-primary" @click="applyFilters" color="secondary" icon="check" label="Apply Filters"
                    no-caps />
            </div>
        </div>

        <!-- Summary Banner -->
        <div class="sroi-summary-banner">
            <div class="banner-header">
                <div class="banner-title-section">
                    <h1 class="banner-title">Purapura Whetu - SROI Analytics</h1>
                    <p class="banner-subtitle">Social Return on Investment insights and metrics</p>
                    <p class="banner-date-range" v-if="summary.dateRange">Data period: {{ summary.dateRange }}</p>
                </div>
                <q-btn 
                    color="black" 
                    icon="settings" 
                    label="Update Values" 
                    @click="showUpdateDialog = true"
                    no-caps
                    flat
                    outline
                />
            </div>
            
            <div class="metrics-grid" v-if="!summaryLoading">
                <div 
                    v-for="(metric, index) in metrics" 
                    :key="index" 
                    class="metric-card"
                >
                    <div class="metric-content">
                        <div class="metric-info">
                            <p class="metric-label text-bold">{{ metric.label }}</p>
                            <p class="metric-value">{{ metric.value }}</p>
                        </div>
                        <div class="metric-icon">
                            <q-icon :name="metric.icon" size="32px" />
                        </div>
                    </div>
                </div>
            </div>
            
            <!-- Loading State -->
            <div v-if="summaryLoading" class="loading-container">
                <q-spinner size="40px" color="primary" />
                <p class="loading-text">Loading SROI data...</p>
            </div>
        </div>

        <!-- SROI Update Dialog -->
        <SroiUpdateComponent v-model="showUpdateDialog" @values-updated="loadSummary" />
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import api from "../../services/api/api";
import notify from '@/services/util/notify';
import dayjs from '@/services/util/dayjs';
import SroiUpdateComponent from './SroiUpdateComponent.vue';

export default {
    name: "SroiComponent",
    components: { SroiUpdateComponent },

    data() {
        return {
            dayjs: dayjs,
            dateFormat: 'YYYY-MM-DD',
            startDate: "",
            endDate: "",
            showUpdateDialog: false
        }
    },

    setup() {
        // Reactive data
        const summary = ref({
            totalSroi: 0,
            formsCompleted: 0,
            participants: 0,
            averageSroiPerForm: 0,
            dateRange: ''
        });

        const summaryLoading = ref(false);

        // Computed metrics for summary banner
        const metrics = computed(() => [
            { 
                label: 'Total SROI', 
                value: summary.value.totalSroi ? `$${summary.value.totalSroi.toLocaleString()}` : '$0', 
                icon: 'bar_chart',
                color: 'text-green-600' 
            },
            { 
                label: 'Forms Completed', 
                value: summary.value.formsCompleted || 0, 
                icon: 'description',
                color: 'text-blue-600' 
            },
            { 
                label: 'Participants', 
                value: summary.value.participants || 0, 
                icon: 'people',
                color: 'text-purple-600' 
            },
            { 
                label: 'Avg SROI per Form', 
                value: summary.value.averageSroiPerForm ? `$${summary.value.averageSroiPerForm.toLocaleString()}` : '$0', 
                icon: 'trending_up',
                color: 'text-orange-600' 
            },
        ]);

        // Methods
        const loadSummary = async (filters = {}) => {
            summaryLoading.value = true;
            try {
                console.log('Loading SROI summary with filters:', filters);
                const response = await api.sroi.getSroiSummary(filters);
                
                if (response.data.success) {
                    summary.value = {
                        totalSroi: response.data.data.total_sroi || 0,
                        formsCompleted: response.data.data.forms_completed || 0,
                        participants: response.data.data.participants || 0,
                        averageSroiPerForm: response.data.data.average_sroi_per_form || 0,
                        dateRange: response.data.data.date_range || ''
                    };
                }
            } catch (error) {
                console.error('Error loading SROI summary:', error);
                notify.error('Failed to load SROI summary data');
            } finally {
                summaryLoading.value = false;
            }
        };

        // Lifecycle
        onMounted(() => {
            loadSummary();
        });

        return {
            summary,
            metrics,
            summaryLoading,
            loadSummary
        };
    },

    methods: {
        applyFilters() {
            const filters = {};

            if (this.startDate) { 
                filters.start_date = this.startDate; 
            }
            if (this.endDate) { 
                filters.end_date = this.endDate; 
            }

            console.log('Applying SROI filters:', filters);
            this.loadSummary(filters);
        },

        resetFilters() {
            this.startDate = "";
            this.endDate = "";
            this.loadSummary();
        }
    }
};
</script>

<style scoped>
.sroi-summary-banner {
    background: linear-gradient(135deg, rgba(136, 166, 143, 0.1) 0%, rgba(136, 166, 143, 0.2) 100%);
    color: #88a68f;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    margin: 1rem;
    border: 1px solid rgba(136, 166, 143, 0.3);
}

.banner-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
}

.banner-title-section {
    flex: 1;
}


.banner-title {
    font-size: 1.875rem;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: #374151;
}

.banner-subtitle {
    color: #374151;
    font-size: 1.125rem;
}

.banner-date-range {
    color: #374151;
    font-size: 0.875rem;
    margin-top: 0.5rem;
}

.metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
}

.metric-card {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(8px);
    border-radius: 8px;
    padding: 1rem;
    border: 1px solid rgba(136, 166, 143, 0.3);
}

.metric-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.metric-info {
    flex: 1;
}

.metric-label {
    color: #374151;
    font-size: 0.875rem;
    margin-bottom: 0.25rem;
}

.metric-value {
    color: #374151;
    font-size: 1.5rem;
    font-weight: bold;
}

.metric-icon {
    opacity: 0.8;
    color: #88a68f;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    color: #374151;
}

.loading-text {
    margin-top: 1rem;
    font-size: 1rem;
    color: #374151;
    opacity: 0.9;
}

@media (max-width: 768px) {
    .metrics-grid {
        grid-template-columns: 1fr;
    }
    
    .banner-title {
        font-size: 1.5rem;
    }
    
    .banner-header {
        flex-direction: column;
        align-items: stretch;
    }
    
    .update-values-btn {
        margin-left: 0;
        margin-top: 1rem;
        align-self: stretch;
    }
}

</style>

<style>
@import '../../assets/styles/clientDemographics.css';
</style>